import React, { useEffect, useMemo, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Row, Col, Input, Empty } from 'antd';
import { FileExcelOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';

import OnTraccrButton from '../common/buttons/OnTraccrButton';
import LiveFeedTextInput from '../dashboard/LiveFeed/LiveFeedTextInput';

import NotesRow from './NotesRow';
import NoteEditModal from './NoteEditModal';
import { exportNotes, parseNote } from './notes.helpers';

import Permissions from '../auth/Permissions';
import { includesTerm, getIdMap } from '../helpers/helpers';
import LiveFeedFileUpload from '../dashboard/LiveFeed/LiveFeedFileUpload';
import PostDrawer from '../dashboard/LiveFeed/PostDrawer';

export default function Notes({
  notes = {},
  id,
  getNotes,
  addNote,
  style = {},
  headerStyle = {},
  authorKey = 'userId',
  users: propUsers = [],
  userTrie: propUserTrie = {},
  allowTags,
  unreadSet = new Set(),
  customerNoteRender,
  canSend = true,
  allowUpload = false,
  type = '',
  getAttachments,
  canEditNotes = false,
  editNote,
  deleteNote,
  exportTitle,
  enabledExport = false,
}) {
  const dispatch = useDispatch();

  const users = useSelector(state => state.users.users);
  const notesFileMap = useSelector((state) => {
    if (type === 'cardNotes') return state.boards.notesFileMap;
    if (type === 'projectNotes') return state.projects.notesFileMap;
    return {};
  });
  const [searchStr,setSearchStr] = useState();
  const [files, setFiles] = useState([]);
  const [selectedPost, setSelectedPost] = useState();
  const [editingNote, setEditingNote] = useState();

  const addFile = (file) => {
    setFiles([...files, file]);
  };
  const onSearch = useCallback((e) => {
    const {
      target: {
        value,
      } = {},
    } = e;
    setSearchStr(value);
  },[]);

  const onSend = useCallback(async ({ text, tags }) => {
    const result = await dispatch(addNote({
      id,
      text,
      tags: (allowTags ? tags : undefined),
      files,
    }));
    if (result) {
      if (getNotes) dispatch(getNotes(id));
      if (getAttachments) dispatch(getAttachments(id));
    }
    return result;
  },[dispatch, id, allowTags, files]);

  useEffect(() => {
    if(getNotes) dispatch(getNotes(id));
  },[dispatch,id]);
  const clearSelectedPost = () => setSelectedPost();
  const [uploadStarted, setUploadStarted] = useState(false);
  const toggleUpload = () => setUploadStarted(!uploadStarted);
  const clearFiles = () => setFiles([]);
  const endUpload = () => setUploadStarted(false);
  const removeFile = (index) => {
    const newFiles = [...files];
    newFiles.splice(index, 1);
    setFiles(newFiles);
  };
  const userMap = useMemo(() => getIdMap(users),[users]);

  const canModifyNotes = canEditNotes && (!!editNote || !!deleteNote);
  const closeEditNote = () => setEditingNote();

  const onSaveNote = useCallback(async ({
    text,
    tags,
    files: nextFiles = [],
    removedFileIds = [],
  }) => {
    if (!editNote || !editingNote) return false;
    const result = await dispatch(editNote({
      id,
      noteId: editingNote.id,
      text,
      tags: allowTags ? tags : (editingNote.tags || []),
      files: nextFiles,
      removedFileIds,
    }));
    if (result && (nextFiles.length > 0 || removedFileIds.length > 0)) {
      if (getNotes) await dispatch(getNotes(id));
      if (getAttachments) dispatch(getAttachments(id));
    }
    return result;
  }, [editNote, editingNote, id, allowTags, getNotes, getAttachments]);

  const onDeleteNote = useCallback(async (note) => {
    if (!deleteNote) return;
    const result = await dispatch(deleteNote({
      id,
      noteId: note.id,
    }));
    if (result && getAttachments) dispatch(getAttachments(id));
  }, [deleteNote, getAttachments, id]);

  const onExportNotes = useCallback(() => {
    const {
      [id]: notesList = [],
    } = notes;
    exportNotes({
      notesList,
      exportTitle,
      authorKey,
      userMap,
    });
  }, [exportTitle, notes, id, authorKey, userMap]);

  const ourNotes = useMemo(() => {
    const {
      [id]: notesList = [],
    } = notes;
    const parsedNotes = [];
    notesList.forEach((noteObject) => {
      const parsedNote = parseNote({
        noteObject,
        authorKey,
        userMap,
        currentUserId: Permissions.id,
      });
      const { note, authorName, isCurrentUser } = parsedNote;
      if (includesTerm(note, searchStr)
        || includesTerm(authorName, searchStr)
        || (searchStr === 'me' && isCurrentUser)) {
        parsedNotes.push(parsedNote);
      }
    });
    parsedNotes.sort((a,b) => b.timestamp - a.timestamp);
    return parsedNotes;
  }, [notes, id, searchStr, userMap, authorKey]);

  return (
    <div className='customer-project-container' style={style}>
      <Row className='customer-project-search-row' justify='space-between' align='middle' style={headerStyle}>
        <Col span={10}>
          <Input.Search
            type='search'
            style={{ width:'100%', maxWidth: 300 }}
            className='searchbar'
            placeholder='Search'
            allowClear
            onChange={onSearch}
            onPressEnter={(e) => e.preventDefault()}
          />
        </Col>
        {enabledExport && (
          <Col>
            <OnTraccrButton
              title="Export"
              type="cancel"
              icon={<FileExcelOutlined />}
              iconLeft
              onClick={onExportNotes}
            />
          </Col>
        )}
      </Row>
      <div className='customer-project-scoller'>
        {ourNotes.map((note, index) => (
          customerNoteRender
          ? customerNoteRender(note, index)
          : (
            <NotesRow
              key={note.id}
              onPostClick={setSelectedPost}
              {...note}
              hideDivider={index === ourNotes.length - 1}
              unread={unreadSet.has(note.id)}
              type={type}
              canEdit={canModifyNotes}
              onEdit={editNote ? () => setEditingNote(note) : undefined}
              onDelete={deleteNote ? () => onDeleteNote(note) : undefined}
            />
          )
        ))}
        {ourNotes.length > 0 && <div style={{ height: 100 }} />}
        {
          ourNotes.length === 0 &&
          <Row justify='center' align='middle' className='customer-project-empty'>
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE}/>
          </Row>
        }
      </div>
      <PostDrawer post={selectedPost} onClose={clearSelectedPost} />
      {editNote && (
        <NoteEditModal
          note={editingNote}
          onClose={closeEditNote}
          onSave={onSaveNote}
          users={propUsers}
          userTrie={propUserTrie}
          userMap={userMap}
          allowTags={allowTags}
          allowUpload={allowUpload}
          fileMap={notesFileMap}
        />
      )}
      {canSend &&
      <>
      <LiveFeedFileUpload
        shouldDownload
        onUploadEnd={toggleUpload}
        addFile={addFile}
        visible={uploadStarted}
        customProps={{ getContainer: false }}
      />
      <LiveFeedTextInput
        onSend={onSend}
        files={files}
        users={allowTags ? propUsers : []} // Need this here or LiveFeedTextInput will go into infinite loop
        userTrie={allowTags ? propUserTrie : {}} // Need this here or LiveFeedTextInput will go into infinite loop
        placeholder='Add a note'
        collapsable
        allowUpload={allowUpload}
        onUpload={toggleUpload}
        onUploadEnd={endUpload}
        uploading={uploadStarted}
        onFileRemove={removeFile}
        clearFiles={clearFiles}
      />
      </>}
    </div>
  );
}

Notes.propTypes = {
  notes: PropTypes.object,
  id: PropTypes.string,
  getNotes: PropTypes.func,
  addNote: PropTypes.func,
  style: PropTypes.object,
  headerStyle: PropTypes.object,
  authorKey: PropTypes.string,
  users: PropTypes.array,
  userTrie: PropTypes.object,
  allowTags: PropTypes.bool,
  unreadSet: PropTypes.instanceOf(Set),
  customerNoteRender: PropTypes.func,
  canSend: PropTypes.bool,
  allowUpload: PropTypes.bool,
  type: PropTypes.string,
  getAttachments: PropTypes.func,
  canEditNotes: PropTypes.bool,
  editNote: PropTypes.func,
  deleteNote: PropTypes.func,
  exportTitle: PropTypes.string,
  enabledExport: PropTypes.bool,
};
